import type { components } from "../../lib/api-types.gen";

/**
 * Pure, React-free helpers for the admin master-data management surface (F-009):
 * endpoint paths, query keys, cache-invalidation targets, and the form-value ->
 * request-body builders. Kept side-effect-free so they can be unit-tested
 * without a DOM or a live query client (the project's node/Vitest style).
 */

/** The four master-data kinds, in the order they appear in the admin tabs. */
export const MASTER_DATA_KINDS = [
  "categories",
  "areas",
  "severities",
  "tags",
] as const;

export type MasterDataKind = (typeof MASTER_DATA_KINDS)[number];

/* ---------------------------------------------------------------------------
 * Endpoint paths (see api/openapi.json)
 * ------------------------------------------------------------------------- */

/**
 * GET /admin/{kind} - management list. Absent includeInactive means the server
 * returns active + inactive; the admin screen wants every row, so no param.
 */
export function adminListPath(kind: MasterDataKind): string {
  return `/admin/${kind}`;
}

/** POST /admin/{kind} - create. */
export function adminCreatePath(kind: MasterDataKind): string {
  return `/admin/${kind}`;
}

/** PATCH /admin/{kind}/{id} - update. */
export function adminItemPath(kind: MasterDataKind, id: string): string {
  return `/admin/${kind}/${id}`;
}

/** POST /admin/{kind}/{id}/deactivate. */
export function adminDeactivatePath(kind: MasterDataKind, id: string): string {
  return `/admin/${kind}/${id}/deactivate`;
}

/** POST /admin/{kind}/{id}/activate. */
export function adminActivatePath(kind: MasterDataKind, id: string): string {
  return `/admin/${kind}/${id}/activate`;
}

/** Public ACTIVE-ONLY selection list backing the new-incident picker. */
export function activePickerPath(kind: MasterDataKind): string {
  return `/${kind}?includeInactive=false`;
}

/* ---------------------------------------------------------------------------
 * Query keys + invalidation
 * ------------------------------------------------------------------------- */

export const adminMasterDataKeys = {
  all: ["admin-master-data"] as const,
  list: (kind: MasterDataKind) => ["admin-master-data", kind] as const,
  // Under the tags list, so every tag change refreshes it by prefix.
  staleTags: ["admin-master-data", "tags", "stale"] as const,
};

/**
 * A successful admin change must refresh both the admin management list AND the
 * public master-data queries that ground the incident screens - the full list
 * (["<kind>"], name resolution) and the active-only picker (["<kind>",
 * "active"]). The bare ["<kind>"] key is a prefix of the active variant, so
 * TanStack Query's prefix match invalidates both from one entry.
 */
export function masterDataInvalidationKeys(
  kind: MasterDataKind,
): readonly (readonly unknown[])[] {
  return [adminMasterDataKeys.list(kind), [kind]];
}

/* ---------------------------------------------------------------------------
 * Form-value coercion
 *
 * The dialog form keeps every field as a trimmed string. These turn a raw
 * string into the JSON shape the API expects: an empty text field becomes
 * `null`, an empty number/select is omitted (create) or nulled (where the API
 * models it as nullable). Whitespace-only is treated as empty.
 * ------------------------------------------------------------------------- */

/** Non-empty trimmed string, else null (nullable text columns). */
export function textOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** Parsed finite number, else null (empty or non-numeric). */
export function numberOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/** A selected id, or null when the "none" option ("") is chosen. */
export function idOrNull(value: string): string | null {
  return value === "" ? null : value;
}

/* ---------------------------------------------------------------------------
 * Per-kind body builders (create + update share a shape here; the API models
 * update fields as optional, and sending the full set is a valid PATCH).
 * ------------------------------------------------------------------------- */

export type MasterDataFormValues = Record<string, string>;

type CategoryBody = components["schemas"]["CreateCategoryRequest"];
type AreaBody = components["schemas"]["CreateAreaRequest"];
type SeverityBody = components["schemas"]["CreateSeverityRequest"];
type TagBody = components["schemas"]["CreateTagRequest"];

export function buildCategoryBody(values: MasterDataFormValues): CategoryBody {
  const rank = numberOrNull(values.rank ?? "");
  return {
    name: (values.name ?? "").trim(),
    description: textOrNull(values.description ?? ""),
    ...(rank !== null ? { rank } : {}),
    symbolHint: textOrNull(values.symbolHint ?? ""),
    parentId: idOrNull(values.parentId ?? ""),
  };
}

export function buildAreaBody(values: MasterDataFormValues): AreaBody {
  const rank = numberOrNull(values.rank ?? "");
  return {
    name: (values.name ?? "").trim(),
    description: textOrNull(values.description ?? ""),
    ...(rank !== null ? { rank } : {}),
    responsibleUserId: idOrNull(values.responsibleUserId ?? ""),
  };
}

export function buildSeverityBody(values: MasterDataFormValues): SeverityBody {
  const rank = numberOrNull(values.rank ?? "");
  return {
    name: (values.name ?? "").trim(),
    description: textOrNull(values.description ?? ""),
    ...(rank !== null ? { rank } : {}),
    recommendedReactionHours: numberOrNull(values.recommendedReactionHours ?? ""),
    colorHint: textOrNull(values.colorHint ?? ""),
  };
}

export function buildTagBody(values: MasterDataFormValues): TagBody {
  return {
    name: (values.name ?? "").trim(),
    description: textOrNull(values.description ?? ""),
  };
}

/** The body builder for each kind, keyed for the section wiring. */
export const bodyBuilders: Record<
  MasterDataKind,
  (values: MasterDataFormValues) => CategoryBody | AreaBody | SeverityBody | TagBody
> = {
  categories: buildCategoryBody,
  areas: buildAreaBody,
  severities: buildSeverityBody,
  tags: buildTagBody,
};

/* ---------------------------------------------------------------------------
 * Search, sort and filter of a management list (F-009, AC-071)
 *
 * The lists are small (a few dozen entries per kind) and already loaded in
 * full, so this runs in the browser over the loaded rows - the server adds
 * nothing a round trip would buy.
 * ------------------------------------------------------------------------- */

/** Which rows the status filter keeps. */
export type MasterDataStatusFilter = "all" | "active" | "inactive";

export type SortDirection = "asc" | "desc";

export interface MasterDataSort {
  key: string;
  dir: SortDirection;
}

/** What the admin has chosen: a search term, a status, and a sort or none. */
export interface MasterDataView {
  q: string;
  status: MasterDataStatusFilter;
  /** Null keeps the server's order (rank, then name). */
  sort: MasterDataSort | null;
}

export const DEFAULT_MASTER_DATA_VIEW: MasterDataView = {
  q: "",
  status: "all",
  sort: null,
};

/** A column's comparable value; null sorts last in either direction. */
export type SortValue = string | number | null;

interface ViewableRow {
  name: string;
  description?: string | null;
  active: boolean;
}

function normalize(text: string): string {
  return text.toLocaleLowerCase("de-DE").trim();
}

/** Does the row match the search term? Name and description, case-free. */
export function matchesMasterDataSearch(row: ViewableRow, q: string): boolean {
  const term = normalize(q);
  if (term === "") return true;
  if (normalize(row.name).includes(term)) return true;
  return row.description ? normalize(row.description).includes(term) : false;
}

function compareValues(a: SortValue, b: SortValue): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "de-DE", { sensitivity: "base", numeric: true });
}

/**
 * The rows the list shows under `view`: filtered by status and search term,
 * then sorted by the chosen column. Empty values sort last in both
 * directions - a missing rank is not "smaller" than rank 0. Ties keep the name
 * order, so the result never depends on the server's row order.
 */
export function viewMasterDataRows<Row extends ViewableRow>(
  rows: readonly Row[],
  view: MasterDataView,
  sortValues: Readonly<Record<string, (row: Row) => SortValue>>,
): Row[] {
  const kept = rows.filter(
    (row) =>
      (view.status === "all" || row.active === (view.status === "active")) &&
      matchesMasterDataSearch(row, view.q),
  );
  const accessor = view.sort ? sortValues[view.sort.key] : undefined;
  if (!view.sort || !accessor) return kept;
  const sign = view.sort.dir === "asc" ? 1 : -1;
  return kept
    .map((row, index) => ({ row, index, value: accessor(row) }))
    .sort((a, b) => {
      const aEmpty = a.value === null || a.value === "";
      const bEmpty = b.value === null || b.value === "";
      if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
      const byValue = aEmpty ? 0 : sign * compareValues(a.value, b.value);
      if (byValue !== 0) return byValue;
      const byName = compareValues(a.row.name, b.row.name);
      return byName !== 0 ? byName : a.index - b.index;
    })
    .map((entry) => entry.row);
}

/**
 * Header click: the active column flips its direction; another column starts
 * ascending - except counts and points in time, where the largest or newest
 * first is what a click asks for.
 */
export function nextMasterDataSort(
  current: MasterDataSort | null,
  key: string,
  descFirst: boolean,
): MasterDataSort {
  if (current?.key === key) {
    return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  }
  return { key, dir: descFirst ? "desc" : "asc" };
}
